import React, { useCallback, useMemo } from 'react';

import { twMerge } from 'tailwind-merge';

import dayjs from 'dayjs';

import { useField } from 'formik';

import TextButton from 'components/text-button/text-button';
import { createLocale } from 'locales';
import Popover from 'components/pop-over/popover';
import type { Week } from 'utils/date';
import { getWeekByOffset } from 'utils/date';
import Typography from 'components/typography/typography';

interface WeekPickerProps {
  className?: string;
}

const globalLocalize = createLocale('global');

const WeekPicker: React.FC<WeekPickerProps> = ({ className }) => {
  const titleClassName = twMerge('hover:text-gray500 hover:underline');

  const [field, , { setValue }] = useField('current-week');

  const weekList: { weeks: Array<string>; selectedWeekIndex: number } =
    useMemo(() => {
      const length = 5;
      const format = 'YYYY-MM-DD';
      const weeks: Array<string> = Array(2 * length + 1);
      const selectedWeek = [
        dayjs(field.value, format),
        dayjs(field.value, format),
      ];

      for (let i = 0; i < length; i += 1) {
        weeks[i] = getWeekByOffset(selectedWeek as Week, i - length)[0].format(
          format,
        );
        weeks[length + i + 1] = getWeekByOffset(
          selectedWeek as Week,
          i + 1,
        )[0].format(format);
      }

      weeks[length] = selectedWeek[0].format(format);

      const result = { selectedWeekIndex: length, weeks };

      return result;
    }, [field.value]);

  const onDropDownItemClicked = useCallback((week: string) => {
    setValue(week);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const switchToWeekByOffset = useCallback(
    (offset: number) => {
      const fristDayOfselectedWeek = dayjs(field.value, 'YYYY-MM-DD');
      const week = getWeekByOffset(
        [fristDayOfselectedWeek, fristDayOfselectedWeek.add(5, 'day')],
        offset,
      );
      setValue(week[0].format('YYYY-MM-DD'));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.value],
  );

  return (
    <TextButton
      previous={{
        onClick: () => switchToWeekByOffset(-1),
        title: globalLocalize('previous'),
      }}
      next={{
        onClick: () => switchToWeekByOffset(+1),
        title: globalLocalize('next'),
      }}
      className={className}
    >
      <Popover
        button={
          <button type="button" className={titleClassName}>
            {field.value}
          </button>
        }
        position="bottom"
        align="end"
      >
        <div className="flex flex-col min-w-max py-2 gap-y-2 rounded-md shadow-4 max-h-40 overflow-y-scroll">
          {weekList.weeks.map((week, index) => {
            const isSelected = weekList.selectedWeekIndex === index;

            const typographyClassNames = twMerge(
              'w-full h-8 hover:bg-gray100 text-center px-6 py-2',
              isSelected && 'bg-gray100',
            );

            return (
              <button
                key={week}
                type="button"
                onClick={() => onDropDownItemClicked(week)}
              >
                <Typography
                  variant="body-medium"
                  as="p"
                  className={typographyClassNames}
                >
                  {week}
                </Typography>
              </button>
            );
          })}
        </div>
      </Popover>
    </TextButton>
  );
};

export default WeekPicker;
